import { ExternalLink } from 'lucide-react';
import { isValidExternalUrl } from '@hcc/types';
import { SHARED_INPUT_CLASS } from './pageMeta';

/**
 * Authoring panel for an external (redirect + self-certify) module. No pages
 * or questions — just the link the learner opens, the instructions shown above
 * it, and the attestation they tick to self-certify. The counterpart to
 * ModuleSettings for the external kind.
 */
export function ExternalModuleEditor({
  title,
  description,
  url,
  instructions,
  confirmation,
  onTitle,
  onDescription,
  onUrl,
  onInstructions,
  onConfirmation,
}: {
  title: string;
  description: string;
  url: string;
  instructions: string;
  confirmation: string;
  onTitle: (next: string) => void;
  onDescription: (next: string) => void;
  onUrl: (next: string) => void;
  onInstructions: (next: string) => void;
  onConfirmation: (next: string) => void;
}) {
  const urlBad = url.trim() !== '' && !isValidExternalUrl(url);
  return (
    <div className="space-y-3 rounded-token border border-line/10 bg-surface p-3">
      <div className="flex items-start gap-2 rounded-token-sm bg-info-soft/15 p-2.5 text-sm text-info-soft-fg">
        <ExternalLink className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          An external module has no on-platform content. The learner opens the link, completes the
          training on that site, then confirms they have done so (an honesty policy). Their
          confirmation is recorded on the audit trail.
        </span>
      </div>

      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Module title</span>
        <input
          value={title}
          onChange={(e) => onTitle(e.target.value)}
          className={SHARED_INPUT_CLASS}
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Description</span>
        <span className="text-fg-dim"> (shown in the training list)</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          className={SHARED_INPUT_CLASS}
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Training link</span>
        <input
          type="url"
          inputMode="url"
          placeholder="https://training.example.gov.uk/course"
          value={url}
          onChange={(e) => onUrl(e.target.value)}
          className={SHARED_INPUT_CLASS}
          aria-invalid={urlBad}
        />
        {urlBad ? (
          <span className="mt-1 block text-xs text-danger-soft-fg">
            Enter a full web address starting with http:// or https://
          </span>
        ) : null}
      </label>

      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Instructions</span>
        <span className="text-fg-dim"> (shown above the button)</span>
        <textarea
          rows={3}
          placeholder="Complete the course on the linked site, then confirm below."
          value={instructions}
          onChange={(e) => onInstructions(e.target.value)}
          className={SHARED_INPUT_CLASS}
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Confirmation statement</span>
        <span className="text-fg-dim"> (the learner ticks this)</span>
        <input
          value={confirmation}
          onChange={(e) => onConfirmation(e.target.value)}
          className={SHARED_INPUT_CLASS}
        />
      </label>
    </div>
  );
}
